import api from '../config/api';
import endpoints from '../config/endpoints';
import { FeedbackCategory, FeedbackSentiment } from '../types/api';

/**
 * Submits feedback with enriched metadata
 * @param {Object} feedback - Feedback data
 * @param {FeedbackCategory} feedback.category - Category of the feedback
 * @param {string} feedback.message - User's feedback message
 * @param {string} feedback.feature_id - ID of the feature being rated
 * @param {FeedbackSentiment} feedback.sentiment - POSITIVE or NEGATIVE
 * @param {boolean} feedback.requires_followup - Whether user wants to be contacted
 * @returns {Promise} - API response
 */
export const submitFeedback = async (feedback) => {
    try {
        // Add metadata
        const enrichedFeedback = {
            ...feedback,
            metadata: {
                userAgent: navigator.userAgent,
                screenSize: {
                    width: window.innerWidth,
                    height: window.innerHeight
                },
                url: window.location.pathname,
                timestamp: new Date().toISOString()
            }
        };

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.log('Submitting feedback:', enrichedFeedback);
        }

        // Send to API
        const response = await api.post(endpoints.feedback.submit, enrichedFeedback);

        // Optional: Track in analytics
        if (window.analytics) {
            window.analytics.track('Feedback Submitted', enrichedFeedback);
        }

        return response.data;
    } catch (error) {
        console.error('Error submitting feedback:', error);
        throw error;
    }
};

/**
 * Custom hook for handling feedback submission
 */
export const useFeedback = () => {
    const handleFeedbackSubmit = async ({
        rating,
        comment,
        contactConsent,
        source,
        category = FeedbackCategory.UI
    }) => {
        const payload = {
            category,
            message: comment,
            feature_id: source,
            sentiment: rating === 'positive' ? FeedbackSentiment.POSITIVE : FeedbackSentiment.NEGATIVE,
            requires_followup: contactConsent
        };

        return await submitFeedback(payload);
    };

    return { handleFeedbackSubmit };
}; 